import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleLayout } from '../components/layout/ModuleLayout';

import { EnterprisePage } from '../components/layout/EnterprisePage';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import { EnterpriseModal, ModalField, modalInputClass, modalSelectClass, ModalPrimaryButton, ModalSecondaryButton } from '../components/layout/EnterpriseModal';
import { useAppToast } from '../components/AppToast';
import { useSimulationRibbon } from '../hooks/useSimulationRibbon';
import type { SimViewMode } from '../hooks/useSimulationRibbon';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useProjectContext } from '../context/ProjectContext';
import { SimulationService } from '../services/simulationService';
import type { SimulationFilters } from '../services/simulationService';
import { SCENARIO_STATUS_LABELS } from '../types/simulation';
import type { SimulationScenario, ScenarioStatus, ScenarioInput } from '../types/simulation';
import { exportToCSV, exportToPrintPDF } from '../utils/exportTableData';
import { desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';
import { BarChart3, Filter, X, ArrowRight, Search, ChevronDown, Check } from 'lucide-react';
import type { StatusCapsuleItem } from '../components/layout/StatusCapsule';

const INPUT_LABELS: Record<keyof ScenarioInput, string> = {
  process_steps: 'Etapas do Processo',
  avg_cycle_time_minutes: 'Tempo de Ciclo Médio (min)',
  resources: 'Recursos',
  batch_size: 'Tamanho do Lote',
  defect_rate_percent: 'Taxa de Defeitos (%)',
  demand_per_day: 'Demanda Diária',
};

const OUTPUT_LABELS: Record<string, string> = {
  lead_time_minutes: 'Lead Time (min)',
  throughput_per_day: 'Throughput (un/dia)',
  avg_queue_length: 'Fila Média',
  resource_utilization_percent: 'Utilização (%)',
  takt_time_minutes: 'Takt Time (min)',
  bottleneck_step: 'Gargalo',
};

/**
 * 🧪 SimuladorPage — Simulação de Cenários What-If
 */
export const SimuladorPage: React.FC = () => {
  const organizationId = useOrganizationId();
  const { selectedProject } = useProjectContext();
  const queryClient = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();

  // ── UI State ───────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<SimViewMode>('table');
  const [filterSearch, setFilterSearch] = useState('');

  // ── Settings modal state ───────────────────────────
  const [editInput, setEditInput] = useState<ScenarioInput>({
    process_steps: 5,
    avg_cycle_time_minutes: 10,
    resources: 3,
    batch_size: 1,
    defect_rate_percent: 2,
    demand_per_day: 100,
  });

  // ── Compare state ─────────────────────────────────
  const [compareId, setCompareId] = useState<string | null>(null);

  // ── Filter state ───────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<ScenarioStatus | ''>('');


  const queryFilters = useMemo<SimulationFilters>(() => ({
    project_id: selectedProject?.id,
    ...(filterStatus && { status: filterStatus as ScenarioStatus }),
  }), [selectedProject?.id, filterStatus]);

  const scenariosQuery = useQuery({
    queryKey: ['simulation-scenarios', organizationId, selectedProject?.id, queryFilters],
    enabled: Boolean(organizationId),
    queryFn: () => SimulationService.list(organizationId, queryFilters),
    staleTime: 30_000,
  });

  const scenarios = scenariosQuery.data ?? [];
  const selectedScenario = scenarios.find(s => s.id === selectedId) ?? null;
  const compareScenario = scenarios.find(s => s.id === compareId) ?? null;

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['simulation-scenarios'] });
  }, [queryClient]);

  // ── Command: scenario.create ───────────────────────
  const handleCreateScenario = useCallback(async () => {
    try {
      const created = await SimulationService.create(organizationId, {
        name: `Cenário — ${new Date().toLocaleString('pt-BR')}`,
        status: 'draft',
        input: {
          process_steps: 5,
          avg_cycle_time_minutes: 10,
          resources: 3,
          batch_size: 1,
          defect_rate_percent: 2,
          demand_per_day: 100,
        },
      });
      showToast('success', 'Cenário criado com sucesso');
      setSelectedId(created.id);
      handleRefresh();
    } catch (err) {
      console.error('[Simulation] create failed:', err);
      showToast('error', 'Erro ao criar cenário');
    }
  }, [organizationId, handleRefresh, showToast]);

  // ── Command: scenario.save ─────────────────────────
  const handleSave = useCallback(async () => {
    if (!selectedId || !selectedScenario) {
      showToast('info', 'Dados sincronizados');
      handleRefresh();
      return;
    }
    try {
      await SimulationService.update(organizationId, selectedId, {
        name: selectedScenario.name,
      });
      showToast('success', 'Cenário salvo');
      handleRefresh();
    } catch (err) {
      console.error('[Simulation] save failed:', err);
      showToast('error', 'Erro ao salvar cenário');
    }
  }, [organizationId, selectedId, selectedScenario, handleRefresh, showToast]);

  // ── Command: scenario.duplicate ────────────────────
  const handleDuplicate = useCallback(async () => {
    if (!selectedScenario) return;
    try {
      const dup = await SimulationService.create(organizationId, {
        name: `${selectedScenario.name} (cópia)`,
        status: 'draft',
        input: selectedScenario.input,
      });
      showToast('success', 'Cenário duplicado');
      setSelectedId(dup.id);
      handleRefresh();
    } catch (err) {
      console.error('[Simulation] duplicate failed:', err);
      showToast('error', 'Erro ao duplicar cenário');
    }
  }, [organizationId, selectedScenario, handleRefresh, showToast]);

  // ── Command: scenario.delete ───────────────────────
  const handleDelete = useCallback(async () => {
    if (!selectedId) return;
    try {
      await SimulationService.delete(organizationId, selectedId);
      showToast('warning', 'Cenário excluído');
      setSelectedId(null);
      handleRefresh();
    } catch (err) {
      console.error('[Simulation] delete failed:', err);
      showToast('error', 'Erro ao excluir cenário');
    }
  }, [organizationId, selectedId, handleRefresh, showToast]);

  // ── Command: scenario.run ──────────────────────────
  const handleRunSimulation = useCallback(async () => {
    if (!selectedId) return;
    setIsRunning(true);
    try {
      await SimulationService.runSimulation(organizationId, selectedId);
      showToast('success', 'Simulação concluída');
      handleRefresh();
    } catch (err) {
      console.error('[Simulation] run failed:', err);
      showToast('error', 'Erro ao executar simulação');
    } finally {
      setIsRunning(false);
    }
  }, [organizationId, selectedId, handleRefresh, showToast]);

  // ── Command: scenario.stop ─────────────────────────
  const handleStopSimulation = useCallback(async () => {
    setIsRunning(false);
    showToast('warning', 'Simulação interrompida');
  }, [showToast]);

  // ── Command: scenario.settings ─────────────────────
  const handleOpenSettings = useCallback(() => {
    if (!selectedScenario) {
      showToast('info', 'Selecione um cenário para configurar');
      return;
    }
    setEditInput(selectedScenario.input ?? {
      process_steps: 5,
      avg_cycle_time_minutes: 10,
      resources: 3,
      batch_size: 1,
      defect_rate_percent: 2,
      demand_per_day: 100,
    });
    setShowSettingsModal(true);
  }, [selectedScenario, showToast]);

  const handleSubmitSettings = useCallback(async () => {
    if (!selectedId) return;
    setSettingsLoading(true);
    try {
      await SimulationService.update(organizationId, selectedId, {
        input: editInput,
        status: 'draft',
        output: null,
      });
      showToast('success', 'Parâmetros atualizados — execute para recalcular');
      setShowSettingsModal(false);
      handleRefresh();
    } catch (err) {
      console.error('[Simulation] settings failed:', err);
      showToast('error', 'Erro ao salvar parâmetros');
    } finally {
      setSettingsLoading(false);
    }
  }, [organizationId, selectedId, editInput, handleRefresh, showToast]);

  // ── Command: export.excel ──────────────────────────
  const handleExportExcel = useCallback(() => {
    const exportData = scenarios.map(s => ({
      name: s.name,
      status: SCENARIO_STATUS_LABELS[s.status],
      process_steps: s.input?.process_steps ?? '',
      avg_cycle_time: s.input?.avg_cycle_time_minutes ?? '',
      resources: s.input?.resources ?? '',
      lead_time: s.output?.lead_time_minutes ?? '',
      throughput: s.output?.throughput_per_day ?? '',
      utilization: s.output?.resource_utilization_percent ?? '',
    }));
    const cols = [
      { key: 'name', label: 'Cenário' },
      { key: 'status', label: 'Status' },
      { key: 'process_steps', label: 'Etapas' },
      { key: 'avg_cycle_time', label: 'Ciclo (min)' },
      { key: 'resources', label: 'Recursos' },
      { key: 'lead_time', label: 'Lead Time (min)' },
      { key: 'throughput', label: 'Throughput' },
      { key: 'utilization', label: 'Utilização (%)' },
    ];
    exportToCSV(exportData, cols, 'simulacao_cenarios');
    showToast('success', 'Exportação Excel gerada');
  }, [scenarios, showToast]);

  // ── Command: export.pdf ────────────────────────────
  const handleExportPDF = useCallback(() => {
    const exportData = scenarios.map(s => ({
      name: s.name,
      status: SCENARIO_STATUS_LABELS[s.status],
      lead_time: s.output?.lead_time_minutes ?? '—',
      throughput: s.output?.throughput_per_day ?? '—',
      utilization: s.output?.resource_utilization_percent ?? '—',
    }));
    const cols = [
      { key: 'name', label: 'Cenário' },
      { key: 'status', label: 'Status' },
      { key: 'lead_time', label: 'Lead Time (min)' },
      { key: 'throughput', label: 'Throughput (un/dia)' },
      { key: 'utilization', label: 'Utilização (%)' },
    ];
    exportToPrintPDF(exportData, cols, 'Simulação de Cenários');
    showToast('info', 'PDF gerado para impressão');
  }, [scenarios, showToast]);

  // ── Command: toggles ───────────────────────────────
  const handleClearFilters = useCallback(() => { setFilterStatus(''); setFilterSearch(''); }, []);
  const handleToggleDashboard = useCallback(() => setShowDashboard(p => !p), []);

  // ── Command: comparison ────────────────────────────
  const handleOpenComparison = useCallback(() => {
    const completed = scenarios.filter(s => s.status === 'completed');
    if (completed.length < 2) {
      showToast('warning', 'Execute ao menos 2 cenários para comparar');
      return;
    }
    setCompareId(null);
    setShowCompareModal(true);
  }, [scenarios, showToast]);

  // ── Filter dropdown state (portal pattern) ──────────
  type SimDd = 'status' | null;
  const [openDd, setOpenDd] = useState<SimDd>(null);
  const statusBtnRef = useRef<HTMLButtonElement>(null);
  const [ddPos, setDdPos] = useState({ top: 0, left: 0 });

  const toggleDd = useCallback((which: SimDd) => {
    if (!which) return;
    if (statusBtnRef.current) {
      const r = statusBtnRef.current.getBoundingClientRect();
      setDdPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenDd(prev => prev === which ? null : which);
  }, []);

  useEffect(() => {
    if (!openDd) return;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-sim-dd]')) return;
      setOpenDd(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDd]);

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const ddPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const ddItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const STATUS_COLORS: Record<string, string> = { draft: '#94a3b8', ready: '#3b82f6', running: '#f59e0b', completed: '#22c55e', failed: '#ef4444' };
  const activeStatusLabel = filterStatus ? SCENARIO_STATUS_LABELS[filterStatus] : 'Todos';

  const filtersWidget = useMemo(() => (
    <div className="flex items-center gap-1.5">
      {/* Status */}
      <button ref={statusBtnRef} data-sim-dd="" type="button" className={filterBtnClass} onClick={() => toggleDd('status')}>
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <span>{activeStatusLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openDd === 'status' && createPortal(
        <div data-sim-dd="" className={ddPanelClass} style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}>
          <button type="button" className={ddItemClass} onClick={() => { setFilterStatus(''); setOpenDd(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={!filterStatus ? 'opacity-100' : 'opacity-0'} />
          </button>
          {(Object.keys(SCENARIO_STATUS_LABELS) as ScenarioStatus[]).map(s => (
            <button key={s} type="button" className={ddItemClass} onClick={() => { setFilterStatus(s); setOpenDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: STATUS_COLORS[s] ?? '#94a3b8' }} />
              <span className="flex-1">{SCENARIO_STATUS_LABELS[s]}</span>
              <Check size={11} className={filterStatus === s ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar cenário..."
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {filterSearch && (
            <button onClick={() => setFilterSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [filterStatus, filterSearch, openDd, ddPos, activeStatusLabel, toggleDd]);

  // ── Wire ribbon ────────────────────────────────────
  const ribbonConfig = useSimulationRibbon({
    onNewScenario: handleCreateScenario,
    onSave: handleSave,
    onDuplicate: handleDuplicate,
    onDelete: handleDelete,
    hasSelection: Boolean(selectedScenario),
    isRunning,
    onRunSimulation: handleRunSimulation,
    onStopSimulation: handleStopSimulation,
    onExportPDF: handleExportPDF,
    onExportExcel: handleExportExcel,
    onShowComparison: handleOpenComparison,
    onShowDashboard: handleToggleDashboard,
    onRefresh: handleRefresh,
    onSettings: handleOpenSettings,
    viewMode,
    onViewModeChange: setViewMode,
    filtersWidget,
  });

  // ── Dashboard stats ────────────────────────────────
  const completedScenarios = useMemo(() => scenarios.filter(s => s.status === 'completed' && s.output), [scenarios]);
  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { draft: 0, ready: 0, running: 0, completed: 0, failed: 0 };
    for (const s of scenarios) c[s.status] = (c[s.status] || 0) + 1;
    return c;
  }, [scenarios]);

  const bestThroughput = useMemo(() => {
    if (completedScenarios.length === 0) return null;
    return completedScenarios.reduce((best, s) =>
      (s.output!.throughput_per_day > (best.output?.throughput_per_day ?? 0)) ? s : best
    , completedScenarios[0]);
  }, [completedScenarios]);

  const statusColor: Record<string, string> = {
    draft: 'bg-slate-500/10 text-slate-600 border border-slate-500/20 dark:text-slate-400',
    ready: 'bg-blue-500/10 text-blue-600 border border-blue-500/20 dark:text-blue-400',
    running: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
    completed: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:text-emerald-400',
    failed: 'bg-red-500/10 text-red-600 border border-red-500/20 dark:text-red-400',
  };



  // ── Dashboard View ─────────────────────────────────
  const dashboardView = (
    <div className="mb-6 rounded border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800/60">
      <div className="mb-4 flex items-center gap-2">
        <BarChart3 size={16} className="text-slate-500" />
        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Dashboard de Simulação</h3>
        <button onClick={handleToggleDashboard} className="ml-auto p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
          <X size={14} className="text-slate-400" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Cenários</p>
          <p className="mt-1 text-2xl font-bold text-slate-800 dark:text-slate-200">{scenarios.length}</p>
        </div>
        <div className="rounded border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
          <p className="text-[10px] uppercase tracking-wide text-emerald-600">Concluídos</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{statusCounts.completed}</p>
        </div>
        <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Rascunhos</p>
          <p className="mt-1 text-2xl font-bold text-slate-600 dark:text-slate-400">{statusCounts.draft}</p>
        </div>
        <div className="rounded border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-[10px] uppercase tracking-wide text-red-600">Falhos</p>
          <p className="mt-1 text-2xl font-bold text-red-700 dark:text-red-400">{statusCounts.failed}</p>
        </div>
      </div>
      {bestThroughput && bestThroughput.output && (
        <div className="mt-4 rounded border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
          <p className="text-[10px] uppercase tracking-wide text-blue-600">Melhor Throughput</p>
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">{bestThroughput.name}</p>
          <p className="mt-1 text-lg font-bold text-blue-700 dark:text-blue-400">{bestThroughput.output.throughput_per_day} un/dia</p>
        </div>
      )}
    </div>
  );

  // ── Comparison View ────────────────────────────────
  const comparisonSelected = selectedScenario?.status === 'completed' ? selectedScenario : null;
  const comparisonTarget = compareScenario?.status === 'completed' ? compareScenario : null;

  // ── Filtered scenarios ─────────────────────────────
  const filteredScenarios = useMemo(() => {
    let list = scenarios;
    if (filterStatus) list = list.filter(s => s.status === filterStatus);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q));
    }
    return list;
  }, [scenarios, filterStatus, filterSearch]);

  return (
    <ModuleLayout ribbonConfig={ribbonConfig}>
      <EnterprisePage
        title=""
        subtitle=""
        statusItems={[]}
      >
        {showDashboard && dashboardView}

        {viewMode === 'table' ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <DataGridEnterprise
              data={filteredScenarios}
              columns={[
                { key: 'name', label: 'Nome', width: '250px', render: (row) => <span className="font-medium">{row.name}</span> } as DataGridColumn<SimulationScenario>,
                { key: 'status', label: 'Status', width: '130px', render: (row) => (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-[7px] w-[7px] rounded-full" style={{ backgroundColor: STATUS_COLORS[row.status] ?? '#94a3b8' }} />
                    <span className="text-[12px]">{SCENARIO_STATUS_LABELS[row.status]}</span>
                  </span>
                )} as DataGridColumn<SimulationScenario>,
                { key: 'created_at', label: 'Criado em', width: '120px', render: (row) => <span className="text-[12px] text-[var(--text-secondary)]">{new Date(row.created_at).toLocaleDateString('pt-BR')}</span> } as DataGridColumn<SimulationScenario>,
                { key: 'lead_time', label: 'Lead Time', width: '110px', mono: true, render: (row) => <span className="text-[12px]">{row.output?.lead_time_minutes != null ? `${row.output.lead_time_minutes}m` : '—'}</span> } as DataGridColumn<SimulationScenario>,
                { key: 'throughput', label: 'Throughput', width: '120px', mono: true, render: (row) => <span className="text-[12px]">{row.output?.throughput_per_day != null ? `${row.output.throughput_per_day}/dia` : '—'}</span> } as DataGridColumn<SimulationScenario>,
                { key: 'utilization', label: 'Utilização', width: '110px', mono: true, render: (row) => <span className="text-[12px]">{row.output?.resource_utilization_percent != null ? `${row.output.resource_utilization_percent}%` : '—'}</span> } as DataGridColumn<SimulationScenario>,
              ]}
              rowKey={(row) => row.id}
              onRowClick={(row) => { setSelectedId(row.id); setViewMode('detail'); }}
              emptyMessage="Nenhum cenário encontrado"
            />
          </div>
        ) : (
        <div className="flex min-h-0 flex-1">
          {/* Scenario list */}
          <div className="w-80 shrink-0 overflow-auto border-r border-slate-200 dark:border-slate-800">
            {scenariosQuery.isLoading ? (
              <div className="flex items-center justify-center py-20 text-sm text-slate-400">Carregando...</div>
            ) : filteredScenarios.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center text-sm text-slate-400">
                <p>Nenhum cenário criado</p>
                <p className="mt-1 text-xs">Use &quot;Novo Cenário&quot; no ribbon</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredScenarios.map(scenario => (
                  <button
                    key={scenario.id}
                    type="button"
                    onClick={() => setSelectedId(scenario.id)}
                    className={`flex w-full flex-col px-4 py-3 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800/40 ${selectedId === scenario.id ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                  >
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{scenario.name}</span>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`rounded-sm px-2 py-0.5 text-[10px] font-semibold ${statusColor[scenario.status] ?? ''}`}>
                        {SCENARIO_STATUS_LABELS[scenario.status]}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {new Date(scenario.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    {scenario.output && (
                      <div className="mt-1.5 flex gap-3 text-[10px] text-slate-500">
                        <span>LT: {scenario.output.lead_time_minutes}m</span>
                        <span>TP: {scenario.output.throughput_per_day}/d</span>
                        <span>Util: {scenario.output.resource_utilization_percent}%</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
            {selectedScenario ? (
              <div>
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{selectedScenario.name}</h2>
                {selectedScenario.description && (
                  <p className="mt-1 text-xs text-slate-500">{selectedScenario.description}</p>
                )}

                {/* Inputs */}
                <div className="mt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parâmetros de Entrada</h3>
                  {selectedScenario.input ? (
                    <div className="mt-2 grid grid-cols-2 gap-3 lg:grid-cols-3">
                      {(Object.entries(selectedScenario.input) as [keyof ScenarioInput, number][]).map(([key, value]) => (
                        <div key={key} className="rounded border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">{INPUT_LABELS[key] ?? key}</p>
                          <p className="mt-0.5 text-lg font-bold text-slate-800 dark:text-slate-200">{String(value)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-slate-400">Nenhum parâmetro configurado</p>
                  )}
                </div>

                {/* Outputs */}
                {selectedScenario.output && (
                  <div className="mt-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resultados</h3>
                    <div className="mt-2 grid grid-cols-2 gap-3 lg:grid-cols-3">
                      {Object.entries(selectedScenario.output).map(([key, value]) => (
                        <div key={key} className="rounded border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/20">
                          <p className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">{OUTPUT_LABELS[key] ?? key}</p>
                          <p className="mt-0.5 text-lg font-bold text-emerald-800 dark:text-emerald-200">{value != null ? String(value) : '—'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                Selecione um cenário na lista à esquerda
              </div>
            )}
          </div>
        </div>
        )}
      </EnterprisePage>

      {/* ── Settings Modal ────────────────────────── */}
      <EnterpriseModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title="Configurar Parâmetros"
        subtitle={selectedScenario?.name}
        loading={settingsLoading}
        maxWidth="max-w-xl"
        footer={
          <>
            <ModalSecondaryButton onClick={() => setShowSettingsModal(false)} disabled={settingsLoading}>Cancelar</ModalSecondaryButton>
            <ModalPrimaryButton onClick={handleSubmitSettings} loading={settingsLoading}>Salvar Parâmetros</ModalPrimaryButton>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          {(Object.keys(INPUT_LABELS) as (keyof ScenarioInput)[]).map(key => (
            <ModalField key={key} label={INPUT_LABELS[key]}>
              <input
                type="number"
                step="any"
                value={editInput[key]}
                onChange={e => setEditInput(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                className={modalInputClass}
              />
            </ModalField>
          ))}
        </div>
      </EnterpriseModal>

      {/* ── Comparison Modal ──────────────────────── */}
      <EnterpriseModal
        open={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        title="Comparar Cenários"
        subtitle="Selecione dois cenários concluídos para comparação"
        maxWidth="max-w-3xl"
        footer={<ModalSecondaryButton onClick={() => setShowCompareModal(false)}>Fechar</ModalSecondaryButton>}
      >
        <div className="mb-4 flex items-center gap-3">
          <select
            value={selectedId ?? ''}
            onChange={e => setSelectedId(e.target.value || null)}
            className={`${modalSelectClass} flex-1`}
          >
            <option value="">Cenário A</option>
            {scenarios.filter(s => s.status === 'completed').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <ArrowRight size={16} className="text-slate-400 shrink-0" />
          <select
            value={compareId ?? ''}
            onChange={e => setCompareId(e.target.value || null)}
            className={`${modalSelectClass} flex-1`}
          >
            <option value="">Cenário B</option>
            {scenarios.filter(s => s.status === 'completed' && s.id !== selectedId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {comparisonSelected?.output && comparisonTarget?.output ? (
          <div className="space-y-2">
            {(Object.keys(OUTPUT_LABELS) as string[]).map(key => {
              const a = (comparisonSelected.output as Record<string, unknown>)?.[key];
              const b = (comparisonTarget.output as Record<string, unknown>)?.[key];
              const aNum = typeof a === 'number' ? a : null;
              const bNum = typeof b === 'number' ? b : null;
              const diff = aNum != null && bNum != null ? bNum - aNum : null;
              return (
                <div key={key} className="flex items-center gap-3 rounded border border-slate-200 px-4 py-2 dark:border-slate-700">
                  <span className="w-36 text-xs font-medium text-slate-600 dark:text-slate-400">{OUTPUT_LABELS[key]}</span>
                  <span className="w-20 text-right text-sm font-semibold text-slate-800 dark:text-slate-200">{a != null ? String(a) : '—'}</span>
                  <ArrowRight size={12} className="text-slate-300" />
                  <span className="w-20 text-right text-sm font-semibold text-slate-800 dark:text-slate-200">{b != null ? String(b) : '—'}</span>
                  {diff != null && (
                    <span className={`ml-auto text-xs font-bold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                      {diff > 0 ? '+' : ''}{Math.round(diff * 100) / 100}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="py-4 text-center text-xs text-slate-400">Selecione ambos os cenários para ver a comparação</p>
        )}
      </EnterpriseModal>

      <ToastContainer />
    </ModuleLayout>
  );
};
